import { Badge } from "design-system/atoms/Badge"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { cn } from "design-system/utils"
import { ArrowUpRight } from "lucide-react"
import { ActionRequestStatusIcon } from "modules/support/components/entities/ActionRequestEntityRow"
import { IssueReopenedBadge } from "modules/support/components/entities/IssueReopenedBadge"
import {
  IssueUrgencyBadge,
  IssueUrgencyIdentifier,
} from "modules/support/components/entities/IssueUrgencyIdentifier"
import { QuestionStatusIcon } from "modules/support/components/entities/QuestionEntityRow"
import {
  getSupportEntityMarkerId,
  scrollToSupportEntityTimelineRows,
} from "modules/support/lib/supportEntityMarker"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ConversationSupportItemResponseDataSchema } from "schema"
import { z } from "zod"

type Item = z.infer<typeof ConversationSupportItemResponseDataSchema>
type ItemKind = Item["kind"]

const MARKER_CLASS_NAMES = {
  question: {
    text: "text-chart-4",
    border: "border-chart-4/20",
    active: "group-[.active-group]:bg-chart-4",
  },
  actionRequest: {
    text: "text-chart-3",
    border: "border-chart-3/20",
    active: "group-[.active-group]:bg-chart-3",
  },
  issueUpdate: {
    text: "text-support-secondary",
    border: "border-support-secondary/20",
    active: "group-[.active-group]:bg-support-secondary group-[.active-group]:text-foreground",
  },
} as const

type Props = {
  items: Item[]
  onIssueClick: (issueId: string) => void
}

export function SupportEntityMarkers({ items, onIssueClick }: Props) {
  const groups = groupItemsByKind(items)

  return groups.map((group) => (
    <SupportEntityMarkerGroup key={group.kind} items={group.items} onIssueClick={onIssueClick} />
  ))
}

function SupportEntityMarkerGroup({ items, onIssueClick }: Props) {
  const { t } = useTranslation()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const item = items[0]
  if (!item) return null

  const classNames = MARKER_CLASS_NAMES[item.kind]
  const label = getMarkerLabel(item.kind, items.length, t)
  const markerClassName = cn(
    "relative z-10 cursor-pointer rounded-full bg-background px-4 duration-150",
    "group-[.active-group]:text-primary-foreground",
    classNames.active,
  )
  const markerContent = (
    <span className="flex items-center gap-1.5">
      <MarkerIcon item={item} />
      {label}
    </span>
  )
  const scrollToTimelineRows = () => scrollToSupportEntityTimelineRows(items)

  return (
    <div
      data-support-entity-marker
      className={cn(
        "group relative flex w-full items-center justify-center text-xs font-medium",
        classNames.text,
      )}
    >
      {items.map((groupItem) => (
        <span
          key={`${groupItem.kind}-${groupItem.id}`}
          id={getSupportEntityMarkerId(groupItem.kind, groupItem.id)}
          className="pointer-events-none absolute"
        />
      ))}
      <div className={cn("min-w-0 flex-1 border-b", classNames.border)} />
      {item.kind === "issueUpdate" ? (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <button type="button" className={markerClassName} onClick={scrollToTimelineRows}>
              {markerContent}
            </button>
          </PopoverTrigger>
          <PopoverContent
            className={cn(
              "max-h-96 w-96 overflow-y-auto",
              "max-w-[var(--radix-popover-content-available-width)]",
            )}
          >
            <ul className="flex flex-col gap-4">
              {items.map((groupItem) => (
                <IssueDetails
                  key={`${groupItem.kind}-${groupItem.id}`}
                  item={groupItem}
                  onIssueClick={(issueId) => {
                    setIsPopoverOpen(false)
                    onIssueClick(issueId)
                  }}
                />
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      ) : (
        <button type="button" className={markerClassName} onClick={scrollToTimelineRows}>
          {markerContent}
        </button>
      )}
      <div className={cn("min-w-0 flex-1 border-b", classNames.border)} />
    </div>
  )
}

function IssueDetails({ item, onIssueClick }: { item: Item; onIssueClick: Props["onIssueClick"] }) {
  const { t } = useTranslation()

  if (item.kind !== "issueUpdate") return null

  return (
    <li className="flex min-w-0 flex-col gap-2 text-sm">
      <div className="flex items-start justify-between gap-2">
        <strong className="font-medium text-foreground">{item.issue.subject}</strong>
        <button
          type="button"
          className="shrink-0 rounded-sm p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={t("support.entities.marker.viewIssue")}
          onClick={() => onIssueClick(item.issue.id)}
        >
          <ArrowUpRight size={14} />
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <IssueUrgencyBadge urgency={item.issue.urgency} />
        <IssueReopenedBadge issue={item.issue} />
        {item.issue.category && (
          <Badge variant="secondary" className="text-muted-foreground">
            {item.issue.category}
          </Badge>
        )}
      </div>
      <span className="whitespace-pre-wrap text-foreground pt-2.5">{item.summary}</span>
    </li>
  )
}

function MarkerIcon({ item }: { item: Item }) {
  switch (item.kind) {
    case "question":
      return <QuestionStatusIcon item={item} className="text-inherit" hasTooltip={false} />
    case "actionRequest":
      return (
        <ActionRequestStatusIcon status={item.status} className="text-inherit" hasTooltip={false} />
      )
    case "issueUpdate":
      return <IssueUrgencyIdentifier urgency={item.issue.urgency} className="text-inherit" />
  }
}

function groupItemsByKind(items: Item[]) {
  const groups = new Map<ItemKind, Item[]>()

  for (const item of items) {
    const group = groups.get(item.kind) ?? []
    group.push(item)
    groups.set(item.kind, group)
  }

  return Array.from(groups, ([kind, groupItems]) => ({ kind, items: groupItems }))
}

function getMarkerLabel(kind: ItemKind, count: number, t: ReturnType<typeof useTranslation>["t"]) {
  switch (kind) {
    case "question":
      return t("support.entities.marker.questionCreated", { count })
    case "actionRequest":
      return t("support.entities.marker.actionRequestCreated", { count })
    case "issueUpdate":
      return t("support.entities.marker.issueCreated", { count })
  }
}
